import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { QnAStateType } from '@uber/react-qna-lib';
import { styled } from 'baseui';
import { Button } from 'baseui/button';
import { toaster } from 'baseui/toast';
import { HeadingXXLarge, ParagraphMedium } from 'baseui/typography';
import isEqual from 'lodash/isEqual';
import type { Logger } from '..';
import type { SendEventT } from '../analytics/types';
import Loader from '../common/loader';
import { REVIEW_SUGGESTIONS_EVENT_NAMES } from '../common/review-suggestions-side-panel/analytics/event-name';
import { TASK_MODE } from '../common/task-state/constants';
import type { OutputLabelledDataT, VideoOutputLabelledDataT } from '../image/labelling';
import { useTaskBackup } from '../image/labelling/hooks/use-task-backup';
import { ShortcutProvider } from '../image/labelling/providers/shortcut-provider';
import { mergeTaskData } from '../image/labelling/utils/mergeTaskData';
import { useAutoPauseDueToInactivity } from '../libraries/auto-pause/use-auto-pause-due-to-inactivity';
import { useMediaPlayback } from '../libraries/auto-pause/use-media-playback';
import { Studio } from './component/studio';
import TaskHeader from './component/task-header';
import { AUTO_PAUSE_TIMEOUT_MINUTES, AUTO_PAUSE_WARNING_MESSAGE, TRACKING_MEDIA_ID } from './consts/auto-pause';
import { MAJOR_STATES, SUBMIT_TASK_ACTION, TASK_TYPE } from './consts/task';
import { useAssignTask } from './hooks/use-assign-task';
import { useFilteredMergedInput } from './hooks/use-filtered-merged-input';
import { useSubmitTask } from './hooks/use-submit-task';
import { useUserEmail } from './hooks/use-user-email';
import useValidateTrackingDetails from './hooks/use-validate-tracking-details';
import { OBSERVABILITY_API_ENDPOINT } from './observability/constants';
import { createObservabilityController } from './observability/observability-controller';
import type { EventPayload } from './observability/types';
import type { SUBMIT_TASK_ACTION_TYPE } from './types/task';
import { getFeatureFlags } from './utils/feature';
import { refreshSignedUrl } from './utils/refresh-signed-url';
import { signalPauseTask } from './utils/signal-pause-task';
import { getYoloGeneratedData } from './utils';
const TaskContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  marginBottom: $theme.sizing.scale100,
  position: 'relative'
}));
const LoadingContainer = styled('div', {
  display: 'flex',
  height: '100vh',
  justifyContent: 'center',
  alignItems: 'center'
});
const TaskContent = styled('div', {
  display: 'flex',
  height: '100%',
  overflow: 'auto'
});
const TaskBanner = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  backgroundColor: $theme.colors.backgroundPrimary,
  padding: $theme.sizing.scale800,
  boxSizing: 'border-box',
  paddingBottom: '72px'
}));
const WelcomeHeading = styled(HeadingXXLarge, ({
  $theme
}) => ({
  marginBottom: $theme.sizing.scale600,
  color: $theme.colors.contentPrimary
}));
const WelcomeText = styled(ParagraphMedium, ({
  $theme
}) => ({
  marginBottom: $theme.sizing.scale800,
  color: $theme.colors.contentSecondary
}));

// Auto-dismiss window for the "tracking in progress" submit toast, matching the background
// tracking toasts (use-ml-tracking-job-runner) so the submit flow feels consistent.
const TRACKING_SUBMIT_TOAST_AUTO_HIDE_MS = 4000;
const ImageStudioWithQuestionnaire = () => {
  const featureFlags = getFeatureFlags();
  const {
    userEmail,
    isLoading: isUserEmailLoading
  } = useUserEmail();
  const {
    assignTask,
    removeTask,
    isSuccess,
    isPending,
    data: assignedTask
  } = useAssignTask(userEmail);
  const taskId = assignedTask?.task.id;
  const {
    isPending: isSubmitTaskPending,
    submitTask,
    error: submitError
  } = useSubmitTask(taskId);
  const {
    updateBackup,
    getBackup,
    cleanBackup
  } = useTaskBackup();
  const filteredAndMergedInput = useFilteredMergedInput(taskId, assignedTask?.task, getBackup);
  const rejectCommentRef = useRef<string>('');
  const isTaskRejectedRef = useRef<boolean | undefined>(false);
  const taskOutput = useRef<OutputLabelledDataT>();
  const isLabelValid = useRef<Boolean>();
  const isQuestionnaireValid = useRef<Boolean>();
  const input = assignedTask?.task.input;
  const isMetaReviewRejectEnabled = !!assignedTask?.project.nextStateOnMetaReviewReject && assignedTask?.task.state === MAJOR_STATES.META_REVIEW;
  const isReviewMode = !!(assignedTask?.task.state === MAJOR_STATES.REVIEW || assignedTask?.task.state === MAJOR_STATES.META_REVIEW);
  const [currentAnswers, setCurrentAnswers] = useState<{
    [questionnaireName: string]: QnAStateType['answers'];
  }>({});
  const [hasValidatedTrackingDetailsOnce, setHasValidatedTrackingDetailsOnce] = useState<boolean | null>(null);
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(false);
  const resetRefs = () => {
    isTaskRejectedRef.current = undefined;
    rejectCommentRef.current = '';
  };
  const getFrames = useCallback(() => {
    return taskOutput.current?.labellingType === 'VIDEO' ? (taskOutput.current as VideoOutputLabelledDataT)?.frames || [] : [];
  }, []);
  const analyticsController = useMemo(() => {
    if (!assignedTask) {
      return null;
    }
    return createObservabilityController({
      apiEndpoint: OBSERVABILITY_API_ENDPOINT,
      context: 'analytics'
    });
  }, [assignedTask]);
  const sendAnalyticsEvent = useCallback<SendEventT>(event => {
    const {
      action,
      ...rest
    } = event;
    if (!analyticsController || !assignedTask) {
      return;
    }
    const {
      id: taskId,
      state,
      status,
      globalTaskId,
      projectId,
      batchId,
      assignedTo
    } = assignedTask.task;
    analyticsController.sendEvent({
      action: action ?? '',
      taskId,
      state,
      status,
      globalTaskId,
      projectId,
      batchId,
      userID: assignedTo,
      ...rest.payload
    });
  }, [analyticsController, assignedTask]);
  const loggerController = useMemo(() => {
    return createObservabilityController({
      apiEndpoint: OBSERVABILITY_API_ENDPOINT,
      context: 'logger'
    });
  }, []);
  const logger: Logger | undefined = useMemo(() => {
    if (!loggerController) {
      return undefined;
    }
    const taskId = assignedTask?.task.id ?? 'unassigned';
    const projectId = assignedTask?.task.projectId ?? 'unassigned';
    const batchId = assignedTask?.task.batchId ?? 'unassigned';
    const userId = assignedTask?.task.assignedTo ?? 'unassigned';
    return {
      info: (message: string) => loggerController.sendInfo({
        message,
        taskId,
        projectId,
        batchId,
        userId
      }),
      warn: (message: string) => loggerController.sendWarn({
        message,
        taskId,
        projectId,
        batchId,
        userId
      }),
      // context carries caller-supplied error metadata (e.g. stack, component name);
      error: (message: string, context?: {
        [id: string]: string;
      }) => loggerController.sendError({
        message,
        taskId,
        projectId,
        batchId,
        userId,
        errorContext: context
      })
    };
  }, [loggerController, assignedTask]);
  const sendAnalyticsEventPayload = useCallback((payload: EventPayload) => {
    if (!analyticsController || !assignedTask) {
      return;
    }
    const {
      id: taskId,
      state,
      status,
      globalTaskId,
      projectId,
      batchId,
      assignedTo
    } = assignedTask.task;
    analyticsController.sendEvent({
      taskId,
      state,
      status,
      globalTaskId,
      projectId,
      batchId,
      userID: assignedTo,
      ...payload
    });
  }, [analyticsController, assignedTask]);
  const {
    validateTrackingDetails,
    trackingDetails,
    trackingDetailsError,
    trackingDetailsLoading,
    setTrackingDetails
  } = useValidateTrackingDetails({
    taskId: taskId!,
    getFrames,
    startFrameIndex: 0,
    onEvaluate: setHasValidatedTrackingDetailsOnce,
    sendEvent: sendAnalyticsEventPayload
  });
  const checkForStaleValidations = (prevOutput, currentOutput) => {
    setHasValidatedTrackingDetailsOnce(hasValidatedTrackingDetailsOnce => {
      if (hasValidatedTrackingDetailsOnce) {
        return isEqual(prevOutput, currentOutput);
      }
      return hasValidatedTrackingDetailsOnce;
    });
  };
  const onGetTask = useCallback(() => {
    assignTask(undefined, {
      onError: error => {
        toaster.negative(<>Failed fetching error: {error.message}</>, {
          key: 'assignedTaskError'
        });
      }
    });
  }, [assignTask]);

  // Background-tracking activity, bridged out of the studio's isolated jotai store via
  // `onTrackingActiveChange` on <Studio>. Read here to gate submit and suppress auto-pause.
  const [isTrackingActive, setIsTrackingActive] = useState(false);

  // Detect media playback so the inactivity timer is paused while video/audio plays
  const {
    notifyMediaPlayback
  } = useMediaPlayback();
  useEffect(() => {
    return () => notifyMediaPlayback(TRACKING_MEDIA_ID, false);
  }, [notifyMediaPlayback]);
  const onTrackingActiveChange = useCallback((isActive: boolean) => {
    setIsTrackingActive(isActive);
    notifyMediaPlayback(TRACKING_MEDIA_ID, isActive);
  }, [notifyMediaPlayback, setIsTrackingActive]);
  const onSubmitTask = useCallback((action: SUBMIT_TASK_ACTION_TYPE, pausedDueToInactivity?: boolean) => {
    if (!taskOutput.current) {
      return;
    }
    if (!assignedTask) {
      return;
    }
    if (isTrackingActive && action === SUBMIT_TASK_ACTION.COMPLETE) {
      toaster.info(<>Tracking is still running. Please wait for it to finish before submitting.</>, {
        key: 'submitTaskError',
        autoHideDuration: TRACKING_SUBMIT_TOAST_AUTO_HIDE_MS
      });
      return;
    }
    if (!isLabelValid.current && action === SUBMIT_TASK_ACTION.COMPLETE) {
      toaster.negative(<>There still issues in labeled data. Please check pending item list.</>, {
        key: 'submitTaskError'
      });
      return;
    }
    if (!isQuestionnaireValid.current && assignedTask.project.taskLevelQuestionnaire && assignedTask.project.taskLevelQuestionnaire.length > 0 && action === SUBMIT_TASK_ACTION.COMPLETE) {
      toaster.negative(<>There are issues in questionnaire.</>, {
        key: 'submitTaskError'
      });
      return;
    }
    const outputWithTaskAnswers = (assignedTask.project.taskLevelQuestionnaire ?? []).reduce<{
      [id: string]: QnAStateType;
    }>((output, {
      questionnaireId
    }) => {
      // @ts-expect-error todo(ts-migration) TS2339 Property 'questionnaire' does not exist on type '{ questionnaire: QnAStateType; name: string; } | undefined'.
      const {
        questionnaire,
        name
      } = assignedTask.project.configuration.questionnaires[questionnaireId];
      output[questionnaireId] = {
        ...questionnaire,
        answers: currentAnswers[name]
      };
      return output;
    }, {});
    const onSubmitSuccess = () => {
      removeTask();
      cleanBackup(assignedTask.task.id.toString());
      resetRefs();
    };
    const onSubmitError = (error: Error) => {
      resetRefs();
      toaster.negative(<>Failed submitting task: {error.message}</>, {
        key: 'submitTaskError'
      });
    };
    if (assignedTask.task.input.labellingType === 'IMAGE' && taskOutput.current.labellingType === 'IMAGE') {
      const mergedOutput = mergeTaskData({
        taskDataInput: {
          ...assignedTask.task.input,
          imageUrl: assignedTask.imageGcsUrl || assignedTask.task.input.imageUrl
        },
        taskDataOutput: {
          ...taskOutput.current,
          ...(!!isTaskRejectedRef.current && action === SUBMIT_TASK_ACTION.COMPLETE && {
            rejectComment: rejectCommentRef.current
          })
        }
      });
      submitTask({
        output: {
          ...mergedOutput,
          taskLevelQuestionnaire: outputWithTaskAnswers
        },
        isRejected: isTaskRejectedRef.current,
        action,
        pausedDueToInactivity: pausedDueToInactivity ?? false
      }, {
        onSuccess: onSubmitSuccess,
        onError: onSubmitError
      });
    }
    if (assignedTask.task.input.labellingType === 'VIDEO' && taskOutput.current.labellingType === 'VIDEO') {
      const mergedOutput = mergeTaskData({
        taskDataInput: {
          ...assignedTask.task.input,
          videoUrl: assignedTask.videoGcsUrl || assignedTask.task.input.videoUrl
        },
        taskDataOutput: {
          ...taskOutput.current,
          ...(!!isTaskRejectedRef.current && action === SUBMIT_TASK_ACTION.COMPLETE && {
            rejectComment: rejectCommentRef.current
          })
        }
      });
      if (featureFlags?.ENABLE_TRACKING_DETAILS_VALIDATION?.enabled && !hasValidatedTrackingDetailsOnce && action === SUBMIT_TASK_ACTION.COMPLETE) {
        sendAnalyticsEventPayload({
          action: REVIEW_SUGGESTIONS_EVENT_NAMES.EVALUATE_TRACKING_DETAILS,
          trigger: 'submit_task'
        });
        validateTrackingDetails();
        setHasValidatedTrackingDetailsOnce(true);
        setShowSuggestionsPanel(true);
        return;
      }
      submitTask({
        output: {
          ...mergedOutput,
          taskLevelQuestionnaire: outputWithTaskAnswers
        },
        isRejected: isTaskRejectedRef.current,
        action,
        pausedDueToInactivity: pausedDueToInactivity ?? false
      }, {
        onSuccess: onSubmitSuccess,
        onError: onSubmitError
      });
    }
  }, [assignedTask, currentAnswers, removeTask, cleanBackup, submitTask, featureFlags?.ENABLE_TRACKING_DETAILS_VALIDATION?.enabled, hasValidatedTrackingDetailsOnce, sendAnalyticsEventPayload, validateTrackingDetails, isTrackingActive]);

  /**
   * Handles the task rejection by the user by setting the task as rejected and the comment in the task output
   */
  const onTaskReject = useCallback((rejectComment: string) => {
    // Set the comment in task output
    rejectCommentRef.current = rejectComment;
    // Set the task as rejected
    isTaskRejectedRef.current = true;
    onSubmitTask(SUBMIT_TASK_ACTION.COMPLETE, false);
  }, [onSubmitTask]);
  const warningToastKeyRef = useRef<React.Key | null>(null);
  const handleAutoPauseWarning = useCallback(() => {
    warningToastKeyRef.current = toaster.warning(AUTO_PAUSE_WARNING_MESSAGE, {
      autoHideDuration: 0
    });
  }, []);
  const handleDismissAutoPauseWarning = useCallback(() => {
    if (warningToastKeyRef.current !== null) {
      toaster.clear(warningToastKeyRef.current);
      warningToastKeyRef.current = null;
    }
  }, []);
  const handleAutoPause = useCallback(() => {
    handleDismissAutoPauseWarning();
    onSubmitTask(SUBMIT_TASK_ACTION.PAUSE, true);
  }, [handleDismissAutoPauseWarning, onSubmitTask]);
  const handleAutoPauseOnPageUnload = useCallback(() => {
    if (taskId && taskOutput.current) {
      signalPauseTask(taskId);
    }
  }, [taskId]);
  const handleAutoPauseBeforeUnload = useCallback(() => {}, []);
  useAutoPauseDueToInactivity({
    timeoutMinutes: AUTO_PAUSE_TIMEOUT_MINUTES,
    onPause: handleAutoPause,
    onWarning: handleAutoPauseWarning,
    onDismissWarning: handleDismissAutoPauseWarning,
    onPageUnload: handleAutoPauseOnPageUnload,
    onBeforeUnload: handleAutoPauseBeforeUnload,
    enabled: !!assignedTask && !!taskId
  });
  const getYoloData = useCallback(() => {
    return getYoloGeneratedData(input);
  }, [input]);
  const getSignedUrl = useCallback(() => {
    if (!taskId) {
      return Promise.resolve(undefined);
    }
    return refreshSignedUrl(taskId);
  }, [taskId]);
  if (submitError) {
    return <>Failed submitting task: {submitError.message}</>;
  }
  if (isUserEmailLoading || isPending || isSubmitTaskPending) {
    return <LoadingContainer>
        <Loader />
      </LoadingContainer>;
  }
  return <ShortcutProvider>
      <TaskContainer>
        <TaskHeader isMetaReviewRejectEnabled={isMetaReviewRejectEnabled} mode={TASK_TYPE.WORK} isTaskVisible={isSuccess} onGetTask={onGetTask} onSubmit={onSubmitTask} userEmail={userEmail} workModeTaskId={taskId} globalTaskId={assignedTask?.task.globalTaskId} batchId={assignedTask?.task.batchId} onTaskReject={onTaskReject} onCommentDialogClose={resetRefs} rejectComment={assignedTask?.task.input.rejectComment} />
        <TaskContent>
          {isSuccess && assignedTask && filteredAndMergedInput ? <Studio sendEvent={sendAnalyticsEvent} logger={logger} taskId={taskId} onTrackingActiveChange={onTrackingActiveChange} inputData={{
          ...filteredAndMergedInput,
          taskState: {
            readOnly: false,
            taskModes: [TASK_MODE.EDIT, ...(isReviewMode ? [TASK_MODE.REVIEW] : [])]
          }
        }} isReviewMode={isReviewMode} annotationLabels={assignedTask.project.configuration.annotationLabel} onStoreChange={(outputLabelledData, validations) => {
          if (assignedTask.task.input.labellingType === 'VIDEO') {
            const prevFrames = (taskOutput.current as VideoOutputLabelledDataT)?.frames;
            const currentFrames = (outputLabelledData as VideoOutputLabelledDataT)?.frames;
            checkForStaleValidations(prevFrames, currentFrames);
          }
          taskOutput.current = outputLabelledData;
          updateBackup(assignedTask.task.id.toString(), outputLabelledData);
          isLabelValid.current = validations.invalidSource || !validations.containsPendingItem && validations.labelValidation.isRequiredLabelAnnotated;
        }}
        // @ts-expect-error todo(ts-migration) TS2322 Type '(questionnaireId: string) => Promise<QnAStateType | undefined>' is not assignable to type '(questionnaireId: string) => Promise<QnAStateType>'.
        fetchQuestionnaire={async (questionnaireId: string) => {
          return assignedTask.project.configuration.questionnaires[questionnaireId]?.questionnaire;
        }} questionnaires={assignedTask.project.configuration.questionnaires} questionnairePreAnnotation={assignedTask.task.input.taskLevelQuestionnaire} onAnswerChange={(updatedAnswer, questionnaireValid) => {
          setCurrentAnswers(updatedAnswer);
          isQuestionnaireValid.current = questionnaireValid;
        }} taskLevelQuestionnaire={assignedTask.project.taskLevelQuestionnaire} getYoloData={getYoloData} getSignedUrl={getSignedUrl} suggestionSidePanelProps={assignedTask.task.input.labellingType === 'VIDEO' ? {
          trackingDetails: trackingDetails,
          suggestionsError: trackingDetailsError ?? null,
          evaluateSuggestionsData: validateTrackingDetails,
          setTrackingDetails,
          panelState: {
            isLoading: !!trackingDetailsLoading,
            hasError: !!trackingDetailsError,
            isStale: !hasValidatedTrackingDetailsOnce
          },
          showSuggestionsPanel: showSuggestionsPanel,
          setShowSuggestionsPanel: setShowSuggestionsPanel
        } : undefined} /> : <TaskBanner>
              <WelcomeHeading>Welcome to uLabel</WelcomeHeading>
              <WelcomeText>Click on Get Task to start</WelcomeText>
              <Button onClick={onGetTask} size="large">
                Get Task
              </Button>
            </TaskBanner>}
        </TaskContent>
      </TaskContainer>
    </ShortcutProvider>;
};
export default ImageStudioWithQuestionnaire;